import { PaginatedResponse, useLoading } from '@gorila-shared-ui/components';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import useUpdateEffect from '../../../../hooks/useUpdateEffect';
import { getSearchAssets } from '../../../../services/assetService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../../storage/app';
import { Asset } from '../../../../types/asset';

export function useAssets(searchAsset: string | undefined, assignedAssetIds: string[]) {
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const apiParams = useRecoilValue(apiParamsState);
  const [selectedAssetPage, setSelectedAssetPage] = useState(1);
  const [unselectedAssetPage, setUnselectedAssetPage] = useState(1);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [unselectedAssets, setUnselectedAssets] = useState<Asset[]>([]);
  const [selectedAssetHasNext, setSelectedAssetHasNext] = useState(false);
  const [unselectedAssetHasNext, setUnselectedAssetHasNext] = useState(false);
  const {
    startLoading: startSelectedAssetLoading,
    stopLoading: stopSelectedAssetLoading,
    loading: selectedAssetLoading,
  } = useLoading();
  const {
    startLoading: startUnselectedAssetLoading,
    stopLoading: stopUnselectedAssetLoading,
    loading: unselectedAssetLoading,
  } = useLoading();

  const getSelectedAssets = async (
    page?: number,
    search?: string,
    assetsSelected?: string[]
  ): Promise<PaginatedResponse<Asset> | undefined> => {
    if (!apiParams?.clientId && !apiParams?.subClientId) return;
    const { assets, error, hasNext, total } = await getSearchAssets(
      apiParams.clientId,
      !isAllWorkspacesSelected ? apiParams.subClientId : undefined,
      page ?? 1,
      search,
      assetsSelected
    );
    if (!error && assets) {
      return {
        items: assets,
        hasNext,
        total,
      };
    }
  };

  const getUnselectedAssets = async (
    page?: number,
    search?: string,
    assetsSelected?: string[]
  ): Promise<PaginatedResponse<Asset> | undefined> => {
    if (!apiParams?.clientId && !apiParams?.subClientId) return;
    const { assets, error, hasNext, total } = await getSearchAssets(
      apiParams.clientId,
      !isAllWorkspacesSelected ? apiParams.subClientId : undefined,
      page ?? 1,
      search,
      undefined,
      assetsSelected
    );
    if (!error && assets) {
      return {
        items: assets,
        hasNext,
        total,
      };
    }
  };

  useEffect(() => {
    if (!assignedAssetIds.length) {
      setSelectedAssets([]);
      setSelectedAssetPage(1);
      return;
    }
    if (selectedAssetPage === 0) {
      setSelectedAssetPage(1);
      return;
    }
    const loadSelectedAssets = async () => {
      startSelectedAssetLoading();
      const response = await getSelectedAssets(selectedAssetPage, searchAsset, assignedAssetIds);
      stopSelectedAssetLoading();
      if (response) {
        setSelectedAssets((prev) => (selectedAssetPage > 1 ? [...prev, ...response.items] : response.items));
        setSelectedAssetHasNext(response.hasNext);
      } else {
        setSelectedAssets([]);
        setSelectedAssetHasNext(false);
      }
    };

    loadSelectedAssets();
  }, [selectedAssetPage]);

  useEffect(() => {
    if (unselectedAssetPage === 0) {
      setUnselectedAssetPage(1);
      return;
    }
    const loadUnselectedAssets = async () => {
      startUnselectedAssetLoading();
      const response = await getUnselectedAssets(unselectedAssetPage, searchAsset, assignedAssetIds);
      stopUnselectedAssetLoading();

      if (response) {
        setUnselectedAssets((prev) => (unselectedAssetPage > 1 ? [...prev, ...response.items] : response.items));
        setUnselectedAssetHasNext(response.hasNext);
      } else {
        setUnselectedAssets([]);
        setUnselectedAssetHasNext(false);
      }
    };

    loadUnselectedAssets();
  }, [unselectedAssetPage]);

  useUpdateEffect(() => {
    setSelectedAssetPage(0);
    setUnselectedAssetPage(0);
  }, [assignedAssetIds, searchAsset]);

  const loadMoreSelectedAssets =
    selectedAssetLoading || !selectedAssetHasNext
      ? () => {}
      : () => {
          setSelectedAssetPage((prev) => prev + 1);
        };
  const loadMoreUnselectedAssets =
    unselectedAssetLoading || !unselectedAssetHasNext
      ? () => {}
      : () => {
          setUnselectedAssetPage((prev) => prev + 1);
        };

  return {
    selectedAssets,
    unselectedAssets,
    selectedAssetHasNext,
    unselectedAssetHasNext,
    selectedAssetLoading,
    unselectedAssetLoading,
    loadMoreSelectedAssets,
    loadMoreUnselectedAssets,
  };
}
